import { usePreservedCallback } from '@toss/react';

import { type CategoryTree } from '@/shared/category/type';
import { SchoolMapper } from '@/shared/category/util';
import {
  type BuildSchemaInputType,
  type BuildSchemaTypeKey,
} from '@/views/build/form/schema';

import { useSelectSchoolPublisher } from '../components/SectionBasicInfo/useSelectSchoolPublisher';
import { useBuildInfoContext } from '../context';

const findNodeInTree = (
  tree: CategoryTree | CategoryTree[],
  targetId: string,
): CategoryTree | undefined => {
  const nodes = Array.isArray(tree) ? tree : [tree];

  for (const node of nodes) {
    if (node.categoryTreeId === targetId) return node;
    if (node.children) {
      const found = findNodeInTree(node.children, targetId);
      if (found) return found;
    }
  }
  return undefined;
};

export const useValidCategory = () => {
  const { schoolInfoTree } = useBuildInfoContext();
  const { publishers: validPublishers } = useSelectSchoolPublisher();
  const { subjectTree } = useBuildInfoContext();

  const getValidateCategoryFields = usePreservedCallback(
    (data: Partial<BuildSchemaInputType>): BuildSchemaTypeKey[] => {
      // 카테고리 키들의 순서는 트리 구조의 계층을 반영하여 의도적으로 정해졌습니다.
      // 상위에서 하위로의 순서: 학교유형 -> 학년 -> 학기 -> 출판사 -> 과목 -> 하위과목
      const categoryOrder: BuildSchemaTypeKey[] = [
        'schoolTypeId',
        'gradeId',
        'semesterId',
        'publisher',
        'customPublisher',
        'subject',
        'subSubject',
      ];

      const validationMap: Partial<Record<BuildSchemaTypeKey, () => boolean>> =
        {
          schoolTypeId: () =>
            !!data.schoolTypeId &&
            !!findNodeInTree(schoolInfoTree, data.schoolTypeId),

          gradeId: () =>
            !!data.gradeId &&
            !!findNodeInTree(schoolInfoTree, data.gradeId.toString()),

          semesterId: () =>
            !!data.semesterId &&
            !!findNodeInTree(schoolInfoTree, data.semesterId.toString()),

          publisher: () =>
            !!data.publisher &&
            (SchoolMapper.isEtc(data.publisher)
              ? !!data.customPublisher
              : validPublishers.some(p => p.value === data.publisher)),

          customPublisher: () =>
            !!data.publisher &&
            SchoolMapper.isEtc(data.publisher) &&
            !!data.customPublisher,

          subject: () =>
            !!data.subject && !!findNodeInTree(subjectTree, data.subject),

          subSubject: () =>
            !!data.subSubject && !!findNodeInTree(subjectTree, data.subSubject),
        };

      // 지정한 키 중에서 유효한 필드만 필터링하여 반환
      return categoryOrder.filter(
        key =>
          data[key] !== undefined &&
          validationMap[key] &&
          validationMap[key]!(),
      );
    },
  );

  return {
    getValidateCategoryFields,
  };
};
